/* ==========================================================================
   FELCO — Pink Blossom landing page (FELS-288)
   Scroll reveals, per-word statement reveal, gift carousel, sticky CTA.
   No dependencies. Safe to load more than once.
   Every init is idempotent and re-runnable, because the theme customizer
   re-renders a section's markup in place and fires shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__pbPinkBlossom) { window.__pbPinkBlossom.init(); return; }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Every IntersectionObserver is kept here for the lifetime of the page.
     An observer held only in a local variable can be collected in some
     engines while it still has observed targets, and then stops firing. */
  var observers = [];

  function keep(observer) {
    observers.push(observer);
    return observer;
  }

  /* --- Scroll reveal ---------------------------------------------------- */
  function initReveals(root) {
    var targets = root.querySelectorAll('[data-pb-reveal]:not([data-pb-reveal-bound])');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-pb-reveal-bound', '');
        el.classList.add('landing-pink-blossom-is-in');
      });
      return;
    }

    var io = keep(new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('landing-pink-blossom-is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }));

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-pb-reveal-bound', '');
      io.observe(el);
    });
  }

  /* --- Per-word reveal on the pink statement ---------------------------- */
  /* Wraps each word in a span and staggers it. Runs on the text node only,
     so inline markup the client adds in the rich-text field survives. */
  function initWords(root) {
    var nodes = root.querySelectorAll('[data-pb-words]:not([data-pb-words-bound])');

    Array.prototype.forEach.call(nodes, function (el) {
      el.setAttribute('data-pb-words-bound', '');

      if (reduceMotion) { el.classList.add('landing-pink-blossom-is-in'); return; }

      wrapWords(el);

      var delay = 0;
      Array.prototype.forEach.call(el.querySelectorAll('.landing-pink-blossom-word'), function (word) {
        word.style.transitionDelay = delay.toFixed(3) + 's';
        delay += 0.028;
      });

      if (!('IntersectionObserver' in window)) { el.classList.add('landing-pink-blossom-is-in'); return; }

      var io = keep(new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('landing-pink-blossom-is-in');
          io.unobserve(entry.target);
        });
      }, { threshold: 0.25 }));
      io.observe(el);
    });
  }

  function wrapWords(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    textNodes.forEach(function (textNode) {
      var frag = document.createDocumentFragment();
      // Keep the whitespace tokens so spacing and line breaks are preserved.
      textNode.nodeValue.split(/(\s+)/).forEach(function (token) {
        if (!token) return;
        if (/^\s+$/.test(token)) {
          frag.appendChild(document.createTextNode(token));
        } else {
          var span = document.createElement('span');
          span.className = 'landing-pink-blossom-word';
          span.textContent = token;
          frag.appendChild(span);
        }
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  /* --- Gift carousel ---------------------------------------------------- */
  function initCarousels(root) {
    var carousels = root.querySelectorAll('[data-pb-carousel]:not([data-pb-carousel-bound])');

    Array.prototype.forEach.call(carousels, function (carousel) {
      carousel.setAttribute('data-pb-carousel-bound', '');

      var slides = carousel.querySelectorAll('.landing-pink-blossom-carousel__slide');
      var dots = carousel.querySelectorAll('.landing-pink-blossom-carousel__dot');
      if (slides.length < 2) {
        // One slide: nothing to page through, so drop the controls entirely.
        var controls = carousel.querySelector('.landing-pink-blossom-carousel__controls');
        if (controls) controls.hidden = true;
        if (slides.length) slides[0].classList.add('is-active');
        return;
      }

      var index = 0;
      var timer = null;
      var hovered = false;
      var interval = parseInt(carousel.getAttribute('data-pb-interval'), 10) || 0;

      function show(next) {
        index = (next + slides.length) % slides.length;
        Array.prototype.forEach.call(slides, function (slide, i) {
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        Array.prototype.forEach.call(dots, function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
          dot.setAttribute('tabindex', i === index ? '0' : '-1');
        });
      }

      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      function start() {
        stop();
        if (interval > 0 && !reduceMotion) {
          timer = setInterval(function () { show(index + 1); }, interval);
        }
      }

      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.addEventListener('click', function () { show(i); start(); });
      });

      // Roving-tabindex keyboard support for the dot group.
      carousel.addEventListener('keydown', function (event) {
        var delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!delta) return;
        event.preventDefault();
        show(index + delta);
        if (dots[index]) dots[index].focus();
        start();
      });

      carousel.addEventListener('mouseenter', function () { hovered = true; stop(); });
      carousel.addEventListener('mouseleave', function () { hovered = false; resume(); });
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', function () { resume(); });

      // Autoplay pauses on hover, on focus within the carousel, and while the
      // tab is hidden. `start` re-checks those, so a mouseleave or a tab
      // returning cannot resume while a dot still has focus.
      function resume() {
        if (hovered || carousel.contains(document.activeElement) || document.hidden) return;
        start();
      }

      // One listener per carousel, removed if the customizer re-renders the
      // section, otherwise every re-render leaves another timer running.
      function onVisibility() { if (document.hidden) { stop(); } else { resume(); } }
      document.addEventListener('visibilitychange', onVisibility);
      if (carousel.__pbCleanup) carousel.__pbCleanup();
      carousel.__pbCleanup = function () {
        stop();
        document.removeEventListener('visibilitychange', onVisibility);
      };

      show(0);
      start();
    });
  }

  /* Last line of defence. If IntersectionObserver has not revealed anything a
     few seconds after load — blocked script, an engine quirk, a browser we did
     not anticipate — show all of it. A late animation is a cosmetic loss;
     copy the visitor never sees is not. */
  function startWatchdog() {
    setTimeout(function () {
      var pending = document.querySelectorAll(
        '[data-pb-reveal]:not(.landing-pink-blossom-is-in), [data-pb-words]:not(.landing-pink-blossom-is-in)'
      );
      if (!pending.length) return;
      Array.prototype.forEach.call(pending, function (el) {
        var box = el.getBoundingClientRect();
        // Only force the ones at or above the fold; the rest may simply not
        // have been scrolled to yet, and those should still animate.
        if (box.top < window.innerHeight) el.classList.add('landing-pink-blossom-is-in');
      });
    }, 2500);
  }

  function init(scope) {
    var root = scope || document;
    // If the boot snippet already gave up and revealed everything, leave it
    // revealed rather than hiding it to animate.
    if (!document.documentElement.hasAttribute('data-pb-revealed')) {
      document.documentElement.classList.add('landing-pink-blossom-js');
    }
    initReveals(root);
    initWords(root);
    initCarousels(root);
    startWatchdog();
  }

  window.__pbPinkBlossom = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme customizer: re-init the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
